/**
 * SEO Configuration & Keywords for Darista
 * Target Market: Tunisia (primary), MENA (secondary)
 * Languages: Arabic (Tunisian), French, English
 */

export const SITE_URL = 'https://darista.tn';
export const SITE_NAME = 'Darista';
export const SITE_PHONE = '+216-24-967-414';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

/**
 * Target Keywords ranked by priority
 * P1 = Highest priority, P3 = Lower priority
 */
export const TARGET_KEYWORDS = {
  // P1 - Brand + Core terms
  brand: {
    ar: ['darista', 'داريستا', 'دريستا'],
    fr: ['darista', 'darista tunisie', 'darista.tn'],
    en: ['darista', 'darista tunisia'],
  },

  // P1 - High-intent commercial keywords
  furniture_general: {
    ar: ['أثاث تونس', 'أثاث منزلي', 'أثاث أون لاين تونس', 'شراء أثاث تونس'],
    fr: [
      'meuble tunisie',
      'meubles en ligne tunisie',
      'mobilier tunisie',
      'meuble pas cher tunisie',
      'achat meuble tunisie',
      'magasin meuble tunisie',
    ],
    en: ['furniture tunisia', 'buy furniture tunisia online', 'home furniture tunisia'],
  },

  // P2 - Category-specific keywords
  kitchen: {
    ar: ['أثاث مطبخ تونس', 'مطبخ عصري', 'تجهيزات المطبخ'],
    fr: ['meuble cuisine tunisie', 'cuisine moderne tunisie', 'meuble cuisine pas cher'],
    en: ['kitchen furniture tunisia', 'modern kitchen tunisia'],
  },
  living_room: {
    ar: ['صالون تونس', 'أثاث غرفة الجلوس', 'صالون عصري'],
    fr: ['salon tunisie', 'meuble salon tunisie', 'salon moderne tunisie', 'séjour tunisie'],
    en: ['living room furniture tunisia', 'sofa tunisia'],
  },
  bedroom: {
    ar: ['أثاث غرفة نوم', 'غرفة نوم تونس', 'سرير تونس'],
    fr: [
      'chambre à coucher tunisie',
      'meuble chambre tunisie',
      'lit tunisie',
      'chambre moderne tunisie',
    ],
    en: ['bedroom furniture tunisia', 'bed tunisia'],
  },
  bathroom: {
    ar: ['أثاث حمام تونس', 'تجهيزات الحمام'],
    fr: ['meuble salle de bain tunisie', 'salle de bain tunisie'],
    en: ['bathroom furniture tunisia'],
  },
  storage: {
    ar: ['خزائن تونس', 'تخزين منزلي', 'رفوف'],
    fr: ['rangement tunisie', 'armoire tunisie', 'étagère tunisie', 'meuble rangement'],
    en: ['storage furniture tunisia', 'shelves tunisia'],
  },

  // P2 - Décor & style
  decor: {
    ar: ['ديكور تونس', 'ديكور منزلي', 'ديكور عصري تونس'],
    fr: ['décoration tunisie', 'décoration intérieure tunisie', 'déco maison tunisie'],
    en: ['home decor tunisia', 'interior design tunisia'],
  },

  // P3 - Long-tail / informational
  longtail: {
    ar: ['أفضل متجر أثاث في تونس', 'توصيل أثاث تونس', 'أثاث بالتقسيط تونس'],
    fr: [
      'meilleur magasin meuble tunisie',
      'livraison meuble tunisie',
      'meuble design tunisie',
      'meuble italien tunisie',
    ],
    en: ['best furniture store tunisia', 'furniture delivery tunisia'],
  },
};

/**
 * Default SEO meta for each page type
 */
export const PAGE_SEO = {
  home: {
    ar: {
      title: 'Darista | أثاث و ديكور المنزل في تونس',
      description:
        'Darista - متجر أثاث و ديكور المنزل الأول في تونس. أثاث المطبخ، غرفة النوم، الصالون و الحمام بأسعار مميزة مع التوصيل لكامل الجمهورية.',
    },
    en: {
      title: 'Darista | Home Furniture & Decor in Tunisia',
      description:
        'Darista - Tunisia\'s leading online furniture store. Kitchen, bedroom, living room & bathroom furniture at great prices with delivery across Tunisia.',
    },
  },
  products: {
    ar: {
      title: 'جميع المنتجات | Darista - أثاث و ديكور تونس',
      description:
        'تصفح مجموعتنا الكاملة من الأثاث و الديكور المنزلي. أثاث المطبخ، الصالون، غرفة النوم و المزيد بأسعار مميزة. Meuble tunisie en ligne.',
    },
    en: {
      title: 'All Products | Darista - Furniture & Decor Tunisia',
      description:
        'Browse our complete collection of home furniture & decor. Kitchen, living room, bedroom furniture and more at great prices. Free delivery across Tunisia.',
    },
  },
  offers: {
    ar: {
      title: 'عروض و تخفيضات | Darista - أفضل أسعار الأثاث في تونس',
      description:
        'اكتشف أحدث العروض و التخفيضات على الأثاث و الديكور المنزلي. خصومات حصرية على أثاث المطبخ، الصالون و غرفة النوم. Promotion meuble tunisie.',
    },
    en: {
      title: 'Offers & Deals | Darista - Best Furniture Prices in Tunisia',
      description:
        'Discover the latest offers and discounts on home furniture & decor. Exclusive deals on kitchen, living room & bedroom furniture.',
    },
  },
  about: {
    ar: {
      title: 'من نحن | Darista - قصتنا و رؤيتنا',
      description:
        'تعرف على Darista - متجر الأثاث و الديكور الأول في تونس. نقدم أجود أنواع الأثاث المنزلي مع خدمة توصيل لكامل الجمهورية.',
    },
    en: {
      title: 'About Us | Darista - Our Story & Vision',
      description:
        'Learn about Darista - Tunisia\'s premier furniture & decor store. We offer premium home furniture with delivery across the country.',
    },
  },
  ourStory: {
    ar: {
      title: 'قصتنا | Darista',
      description: 'اكتشف قصة Darista و كيف بدأنا رحلتنا في عالم الأثاث و الديكور في تونس.',
    },
    en: {
      title: 'Our Story | Darista',
      description: 'Discover the Darista story and how we started our journey in furniture & decor in Tunisia.',
    },
  },
  contact: {
    ar: {
      title: 'اتصل بنا | Darista - خدمة عملاء 24/7',
      description:
        'تواصل معنا عبر الواتساب أو عبر نموذج الاتصال. فريق دعم Darista متاح على مدار الساعة لمساعدتك.',
    },
    en: {
      title: 'Contact Us | Darista - 24/7 Customer Support',
      description:
        'Reach us via WhatsApp or contact form. Darista support team is available around the clock to help you.',
    },
  },
  faq: {
    ar: {
      title: 'الأسئلة الشائعة | Darista',
      description: 'اعثر على إجابات لأسئلتك حول الطلبات، التوصيل، الدفع و سياسة الإرجاع في Darista.',
    },
    en: {
      title: 'FAQ | Darista',
      description: 'Find answers to your questions about orders, delivery, payment and return policy at Darista.',
    },
  },
  shipping: {
    ar: {
      title: 'معلومات التوصيل | Darista - توصيل لكامل الجمهورية',
      description: 'تعرف على خدمات التوصيل من Darista. توصيل سريع لكامل ولايات الجمهورية التونسية.',
    },
    en: {
      title: 'Shipping Info | Darista - Delivery Across Tunisia',
      description: 'Learn about Darista delivery services. Fast shipping to all governorates across Tunisia.',
    },
  },
  returns: {
    ar: {
      title: 'سياسة الإرجاع | Darista',
      description: 'تعرف على سياسة الإرجاع و الاستبدال في Darista. ضمان رضاك هو أولويتنا.',
    },
    en: {
      title: 'Returns Policy | Darista',
      description: 'Learn about Darista return & exchange policy. Your satisfaction is our priority.',
    },
  },
  cart: {
    ar: {
      title: 'سلة التسوق | Darista',
      description: 'راجع منتجاتك في سلة التسوق وأكمل عملية الشراء.',
    },
    en: {
      title: 'Shopping Cart | Darista',
      description: 'Review your cart items and proceed to checkout.',
    },
  },
  wishlist: {
    ar: {
      title: 'قائمة الأمنيات | Darista',
      description: 'احفظ منتجاتك المفضلة في قائمة الأمنيات.',
    },
    en: {
      title: 'Wishlist | Darista',
      description: 'Save your favorite products to your wishlist.',
    },
  },
};

/**
 * Generate product page SEO meta
 */
export const getProductSEO = (product, isArabic) => {
  if (!product || !product.name) return {};

  const priceText = product.price
    ? `${(product.price / 100).toFixed(2)} TND`
    : '';

  if (isArabic) {
    return {
      title: `${product.name} | Darista - أثاث تونس`,
      description: `اشتري ${product.name} من Darista بسعر ${priceText}. توصيل لكامل الجمهورية التونسية. ${product.category || ''}`,
    };
  }

  return {
    title: `${product.name} | Darista - Furniture Tunisia`,
    description: `Buy ${product.name} from Darista at ${priceText}. Delivery across Tunisia. ${product.category || ''}`,
  };
};

/**
 * Generate breadcrumb items for JSON-LD
 */
export const getBreadcrumbItems = (items) => {
  return items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url ? `${SITE_URL}${item.url}` : undefined,
  }));
};

/**
 * Blog/Content Ideas for Organic Traffic
 * (Reference for future content strategy)
 *
 * 1. "أفضل ألوان أثاث المطبخ لسنة 2026" - Best kitchen colors 2026
 * 2. "كيفاش تختار صالون يناسب دارك" - How to choose the right sofa (Tunisian darja)
 * 3. "Guide: Choisir son meuble de salle de bain en Tunisie" - Bathroom furniture guide (French)
 * 4. "5 أفكار ديكور لغرفة نوم صغيرة" - 5 decor ideas for small bedrooms
 * 5. "Tendances meubles 2026 en Tunisie" - Furniture trends 2026
 * 6. "أثاث غرفة الأطفال: نصائح و أفكار" - Kids room furniture tips
 * 7. "Comment aménager un studio en Tunisie" - How to furnish a studio
 * 8. "مقارنة بين أنواع الخشب للأثاث" - Wood types comparison
 * 9. "Salon moderne vs traditionnel tunisien" - Modern vs traditional
 * 10. "دليل شراء أثاث أون لاين في تونس" - Online furniture buying guide
 */
